import React from 'react';
import {Award, Users, Wrench, Target, CheckCircle} from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Award className="w-10 h-10" />,
      title: "Excellence",
      description: "We maintain the highest standards in every repair and service"
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: "Customer First",
      description: "Your satisfaction and convenience are our top priorities"
    },
    {
      icon: <Wrench className="w-10 h-10" />,
      title: "Expertise",
      description: "Fully qualified mechanics with years of experience"
    },
    {
      icon: <Target className="w-10 h-10" />,
      title: "Reliability",
      description: "On-time service you can count on, every time"
    }
  ];

  const features = [
    "Fully qualified and certified mechanics",
    "Comprehensive mobile service coverage",
    "Modern diagnostic equipment",
    "Competitive and transparent pricing",
    "Quality parts and materials",
    "Same-day service available",
    "Emergency breakdown assistance",
    "All work guaranteed"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#333333] to-[#4682B4] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            About <span className="text-[#FFD700]">ONPOINT Mobile Mechanics</span>
          </h1>
          <p className="text-xl text-[#E0E0E0] max-w-3xl mx-auto leading-relaxed">
            Your trusted partner for professional mobile auto repair and maintenance services across Birmingham, Coventry, Wolverhampton and the West Midlands
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.pexels.com/photos/5835264/pexels-photo-5835264.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Professional mechanic inspecting vehicle"
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
            
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-[#333333]">
                Our Story
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                ONPOINT Mobile Mechanics was founded with a simple mission: to bring professional, reliable auto repair services directly to our customers across the West Midlands. Serving Birmingham, Coventry, Wolverhampton, Dudley, Walsall, Solihull and surrounding areas, we understand that your time is valuable and that dealing with car problems can be stressful.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our team of fully qualified mechanics brings years of experience and expertise to every job. We're passionate about keeping your vehicle running smoothly and safely, whether you need routine maintenance or emergency repairs.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With ONPOINT, you don't need to waste time travelling to a garage or waiting in reception areas. We come to you - at home, at work, or even if you're stranded on the roadside.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-[#E0E0E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-4">
              Our Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all text-center"
              >
                <div className="text-[#4682B4] mb-4 flex justify-center">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-[#333333] mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-[#333333]">
                What Sets Us Apart
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                At ONPOINT Mobile Mechanics, we're not just another repair service. We're a team dedicated to providing exceptional mobile mechanic services that put your needs first.
              </p>
              
              <div className="space-y-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-[#4682B4] flex-shrink-0 mt-1" />
                    <span className="text-gray-700 text-lg">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <img 
                src="https://images.pexels.com/photos/4489737/pexels-photo-4489737.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Mechanic using diagnostic equipment on vehicle"
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#4682B4] text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Experience the ONPOINT Difference
          </h2>
          <p className="text-xl mb-8 text-[#E0E0E0]">
            Call us today to schedule your mobile mechanic service
          </p>
          
          <a
            href="tel:07454922145"
            className="bg-[#FFD700] text-[#333333] px-8 py-4 rounded-lg font-bold text-lg hover:bg-white transition-all inline-block shadow-lg"
            aria-label="Call ONPOINT Mobile Mechanics"
          >
            Call 07454 922145
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
